import { useMemo, useState } from 'react';
import { BarChart } from '../charts/BarChart';
import { BarList, ChartCard, Drawer, Segmented, Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num, periodLabel, shortDate, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource, useResource } from '../lib/hooks';

interface Quote {
  id: number;
  quoteNumber: number | null;
  name: string;
  company: string | null;
  created: string | null;
  effective: string | null;
  expires: string | null;
  approvalStatus: string;
  createdById: number | null;
  createdBy: string | null;
  primary: boolean;
  active: boolean;
  opportunity: string | null;
  outcome: string;
  stage: string | null;
  ownerId: number | null;
  owner: string | null;
  amount: number | null;
  monthly: number | null;
  closed: string | null;
}

interface QuoteItem {
  id: number;
  name: string;
  description: string | null;
  type: string | null;
  period: string | null;
  quantity: number;
  unitPrice: number;
  total: number;
  optional: boolean;
}

type Range = 'lastweek' | '30d' | '90d' | '6m' | '12m' | 'ytd' | 'custom';
type Who = 'creator' | 'owner';
/** Which date the period, the chart and the table are read on. */
type Basis = 'created' | 'closed';
/** Which tile the list of quotes is cut to. `kept` is everything but the lost
    ones, and is what the report opens on: a lost quote is still a quote, but it
    is not one of the quotes that landed, so it does not belong in the total. */
type Focus = 'kept' | 'all' | 'won' | 'lost' | 'open' | 'decided';

const FOCUS_LABEL: Record<Focus, string> = {
  kept: '',
  all: '',
  won: 'won ',
  lost: 'lost ',
  open: 'open ',
  decided: 'won or lost ',
};

const BASES: { id: Basis; label: string }[] = [
  { id: 'created', label: 'Created date' },
  { id: 'closed', label: 'Closed date' },
];
/* The word for the basis, for the period's label and the chart's title. */
const BASIS_WORD: Record<Basis, string> = { created: 'created', closed: 'closed' };
/** The date a quote falls on under the chosen basis. */
const basisDate = (q: Quote, basis: Basis) => (basis === 'closed' ? q.closed : q.created);

const RANGES: { id: Range; label: string }[] = [
  { id: 'lastweek', label: 'Last week' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: '6m', label: '6 months' },
  { id: '12m', label: '12 months' },
  { id: 'ytd', label: 'This year' },
  { id: 'custom', label: 'Custom' },
];

/** The Sunday of the week a date falls in — the week here runs Sunday to Saturday. */
function sundayOf(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function rangeStart(range: Exclude<Range, 'custom' | 'lastweek'>): string {
  const d = new Date();
  if (range === 'ytd') return `${d.getFullYear()}-01-01`;
  if (range === '30d') d.setDate(d.getDate() - 29);
  if (range === '90d') d.setDate(d.getDate() - 89);
  if (range === '6m') d.setMonth(d.getMonth() - 6);
  if (range === '12m') d.setMonth(d.getMonth() - 12);
  return toLocalDateString(d);
}

/** The days a range covers. Last week is the whole week before this one,
    Sunday to Saturday; every other range runs back from today. */
function rangeDays(range: Exclude<Range, 'custom'>): { from: string; to: string } {
  if (range === 'lastweek') {
    const sunday = sundayOf(new Date());
    sunday.setDate(sunday.getDate() - 7);
    const saturday = new Date(sunday);
    saturday.setDate(saturday.getDate() + 6);
    return { from: toLocalDateString(sunday), to: toLocalDateString(saturday) };
  }
  return { from: rangeStart(range), to: todayLocal() };
}

const money = (v: number) => formatNumber(v, 'currency');

/* Autotask keeps the decision in two places, and which one carries it depends
   on how the tenant is set up: the opportunity's status and its stage. A
   default setup leaves the status reading "Closed" for a win and a loss alike
   and says which in the stage — Closed Won, Closed Lost — so both are read.
   The picklists are the tenant's own, so these are the words this one uses: a
   quote the client turned down closes as Closed Rejected, which is a loss. */
const LOST_WORDS = /lost|rejected/i;
const WON_WORDS = /closed|won|implemented/i;
/** A stage that names the decision itself, rather than leaving it to the status. */
const DECIDING_STAGE = /won|lost|rejected/i;

const decision = (q: Quote) => `${q.stage ?? ''} ${q.outcome}`;
const isLost = (q: Quote) => LOST_WORDS.test(decision(q));
/* Lost first: Autotask writes a lost opportunity as Closed Lost or Closed
   Rejected, and the word "closed" alone would otherwise read it as a win. */
const isWon = (q: Quote) => !isLost(q) && WON_WORDS.test(decision(q));
const result = (q: Quote) => (isWon(q) ? 'Won' : isLost(q) ? 'Lost' : 'Open');
const inFocus = (q: Quote, f: Focus) =>
  f === 'all' ||
  (f === 'kept' && !isLost(q)) ||
  (f === 'won' && isWon(q)) ||
  (f === 'lost' && isLost(q)) ||
  (f === 'open' && !isWon(q) && !isLost(q)) ||
  (f === 'decided' && (isWon(q) || isLost(q)));

/** How a quote's outcome reads on the breakdown and the chart. Where the stage
    is the field that names the decision, it is the clearer of the two — the
    status says "Closed" for both outcomes, which puts a win and a loss in one
    bar — so the stage wins there and the status is used everywhere else. */
const outcomeLabel = (q: Quote) => (q.stage && DECIDING_STAGE.test(q.stage) ? q.stage : q.outcome);

/** The week (from Sunday) or month a date falls in — the chart's column key. */
function bucket(iso: string, byWeek: boolean): string {
  const d = new Date(iso);
  if (!byWeek) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return toLocalDateString(sundayOf(d));
}

const OUTCOME_COLOURS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)', 'var(--series-5)', 'var(--series-6)'];
const colour = (i: number): string => OUTCOME_COLOURS[i % OUTCOME_COLOURS.length]!;

/**
 * Reports › Quotes — every Autotask quote created in a period and the
 * outcome of its opportunity, filterable by date and
 * by resource (who created the quote, or who owns the opportunity). Read live;
 * see routes/reports.ts for where each figure comes from in Autotask.
 *
 * Won and lost are read from the opportunity's stage as well as its status,
 * because Autotask can carry the decision in either — see `decision` above.
 * The headline count leaves the lost ones out; they have their own tile, and
 * revenue is split won, lost and open rather than shown as one number.
 *
 * Every figure drills down: a tile, an outcome or a chart column cuts the list
 * of quotes at the bottom, and a quote in that list opens with its line items.
 */
export function QuotesReportPage() {
  const [range, setRange] = useState<Range>('90d');
  const [customFrom, setCustomFrom] = useState(rangeStart('90d'));
  const [customTo, setCustomTo] = useState(todayLocal());
  const [basis, setBasis] = useState<Basis>('created');
  const [who, setWho] = useState<Who>('creator');
  const [resource, setResource] = useState('');
  const [focus, setFocus] = useState<Focus>('kept');
  const [outcome, setOutcome] = useState('');
  const [period, setPeriod] = useState('');
  const [search, setSearch] = useState('');
  const [openQuote, setOpenQuote] = useState<Quote | null>(null);
  const clearDrill = () => {
    setFocus('kept');
    setOutcome('');
    setPeriod('');
  };
  /* A tile cuts the list to its own quotes, and cutting it again goes back to
     the whole period. The outcome picked below is cleared with it: the two cut
     the same quotes different ways, and left together they can only disagree —
     "won" and "Closed Lost" at once is an empty list. */
  const pickFocus = (f: Focus) => {
    setFocus(focus === f ? 'kept' : f);
    setOutcome('');
  };
  const pickOutcome = (name: string) => {
    setOutcome(outcome === name ? '' : name);
    setFocus('all');
  };

  const { from: fromDay, to: toDay } = range === 'custom' ? { from: customFrom, to: customTo } : rangeDays(range);
  /* The browser's own days, midnight to midnight, as UTC instants. */
  const from = new Date(`${fromDay}T00:00:00`);
  const to = new Date(`${toDay}T00:00:00`);
  to.setDate(to.getDate() + 1);
  const valid = fromDay && toDay && fromDay <= toDay;

  /* Held: coming back to this page, or back to a period already looked at,
     shows what was read — until Refresh. */
  const data = useHeldResource<{ quotes: Quote[]; fetchedUtc: string }>(
    valid ? `/reports/quotes?${new URLSearchParams({ from: from.toISOString(), to: to.toISOString(), basis })}` : null,
  );
  const all = data.data?.quotes ?? [];

  const people = useMemo(() => {
    const m = new Map<number, string>();
    for (const q of all) {
      const id = who === 'creator' ? q.createdById : q.ownerId;
      const name = who === 'creator' ? q.createdBy : q.owner;
      if (id !== null && name) m.set(id, name);
    }
    return [...m].sort((a, b) => a[1].localeCompare(b[1]));
  }, [all, who]);

  /* The resource filter applies to everything below. */
  const mine = useMemo(
    () => (resource ? all.filter((q) => String(who === 'creator' ? q.createdById : q.ownerId) === resource) : all),
    [all, resource, who],
  );

  const count = (key: (q: Quote) => string) => {
    const m = new Map<string, { n: number; value: number }>();
    for (const q of mine) {
      const k = key(q);
      const e = m.get(k) ?? { n: 0, value: 0 };
      e.n += 1;
      e.value += q.amount ?? 0;
      m.set(k, e);
    }
    return [...m].map(([name, e]) => ({ name, ...e })).sort((a, b) => b.n - a.n);
  };
  const byOutcome = count(outcomeLabel);

  const won = mine.filter(isWon);
  const lost = mine.filter(isLost);
  const open = mine.filter((q) => !isWon(q) && !isLost(q));
  /* The headline count. A lost quote was closed, but it did not land, so it is
     left out of the total rather than padding it. */
  const kept = mine.filter((q) => !isLost(q));
  const winRate = won.length + lost.length ? (won.length / (won.length + lost.length)) * 100 : null;
  const sum = (list: Quote[]) => list.reduce((n, q) => n + (q.amount ?? 0), 0);
  const sumMonthly = (list: Quote[]) => list.reduce((n, q) => n + (q.monthly ?? 0), 0);
  /* The two feet every tile uses: what one quote is worth on average, and what
     part of a revenue total comes back every month rather than once. */
  const average = (list: Quote[], none: string) => (list.length ? `${money(sum(list) / list.length)} average` : none);
  const recurring = (list: Quote[]) =>
    sumMonthly(list) ? `${money(sumMonthly(list))}/mo recurring` : `across ${num(list.length)} quote${list.length === 1 ? '' : 's'}`;

  /* Quotes created per month (or week, for short periods), stacked by outcome. */
  const days = (to.getTime() - from.getTime()) / 86_400_000;
  const byWeek = days <= 92;
  const outcomes = byOutcome.map((o) => o.name).slice(0, 6);
  const trend = useMemo(() => {
    const keys: string[] = [];
    const cur = new Date(from);
    while (cur < to) {
      const k = bucket(cur.toISOString(), byWeek);
      if (keys[keys.length - 1] !== k) keys.push(k);
      cur.setDate(cur.getDate() + 1);
    }
    const rowsOut = keys.map((k) => ({ period: k, ...Object.fromEntries(outcomes.map((o) => [o, 0])) }) as Record<string, string | number>);
    const at = new Map(rowsOut.map((r) => [r.period as string, r]));
    for (const q of mine) {
      const day = basisDate(q, basis);
      if (!day) continue;
      const r = at.get(bucket(day, byWeek));
      const label = outcomeLabel(q);
      if (r && outcomes.includes(label)) r[label] = (Number(r[label]) || 0) + 1;
    }
    return rowsOut;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine, basis, byWeek, outcomes.join('|'), fromDay, toDay]);

  /* The quotes behind whatever was clicked. */
  const needle = search.trim().toLowerCase();
  const rows = mine.filter((q) => {
    const day = basisDate(q, basis);
    return (
      inFocus(q, focus) &&
      (!outcome || outcomeLabel(q) === outcome) &&
      (!period || (day !== null && bucket(day, byWeek) === period)) &&
      (!needle ||
        [q.name, q.company, q.opportunity, q.createdBy, q.owner, q.quoteNumber].some(
          (v) => v !== null && String(v).toLowerCase().includes(needle),
        ))
    );
  });
  const drilled = focus !== 'kept' || outcome !== '' || period !== '';
  const cut = [
    focus === 'kept' && lost.length ? 'lost left out' : '',
    outcome,
    period ? (byWeek ? `week of ${shortDate(period)}` : periodLabel(period)) : '',
  ].filter(Boolean);
  /* A new period has different columns, so a column picked in the old one goes. */
  const newPeriod = () => setPeriod('');

  const exportCsv = () =>
    downloadCsv(`quotes_${fileStamp()}.csv`, [
      ['Quote #', 'Quote', 'Company', 'Created', 'Closed', 'Expires', 'Approval status', 'Outcome', 'Stage', 'Result', 'Opportunity', 'Created by', 'Owner', 'Amount', 'Monthly'],
      ...rows.map((q) => [
        q.quoteNumber, q.name, q.company, q.created?.slice(0, 10), q.closed?.slice(0, 10), q.expires?.slice(0, 10),
        q.approvalStatus, q.outcome, q.stage, result(q), q.opportunity, q.createdBy, q.owner, q.amount, q.monthly,
      ]),
    ]);

  return (
    <>
      <PageHead
        title="Quotes"
        lede="Every Autotask quote in a period — by the date it was created or the date its opportunity closed — whether that opportunity was won, lost or is still open, for everyone or one person. Read live from Autotask."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-4)' }}>
            <Field label="Date">
              <Segmented
                label="Date"
                options={BASES}
                value={basis}
                onChange={(v) => {
                  setBasis(v);
                  newPeriod();
                }}
              />
            </Field>
            <Field label={basis === 'closed' ? 'Closed' : 'Created'}>
              <Segmented
                label={basis === 'closed' ? 'Closed' : 'Created'}
                options={RANGES}
                value={range}
                onChange={(v) => {
                  setRange(v);
                  newPeriod();
                }}
              />
            </Field>
            {range === 'custom' && (
              <>
                <Field label="From">
                  <input
                    className="input"
                    type="date"
                    value={customFrom}
                    max={customTo}
                    onChange={(e) => {
                      setCustomFrom(e.target.value);
                      newPeriod();
                    }}
                  />
                </Field>
                <Field label="To">
                  <input
                    className="input"
                    type="date"
                    value={customTo}
                    min={customFrom}
                    onChange={(e) => {
                      setCustomTo(e.target.value);
                      newPeriod();
                    }}
                  />
                </Field>
              </>
            )}
            <Field label="Resource is">
              <Segmented
                label="Resource is"
                options={[
                  { id: 'creator', label: 'Created by' },
                  { id: 'owner', label: 'Opportunity owner' },
                ]}
                value={who}
                onChange={(v) => {
                  setWho(v);
                  setResource('');
                }}
              />
            </Field>
            <Field label="Resource">
              <select className="select" value={resource} onChange={(e) => setResource(e.target.value)} disabled={!data.data}>
                <option value="">Everyone</option>
                {people.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </Field>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            {data.fetchedAt && (
              <span className="xs muted">
                As of {new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
          {basis === 'closed' && (
            <p className="xs muted" style={{ marginTop: 'var(--space-3)', marginBottom: 0 }}>
              On closed date, a quote counts in the period its opportunity was won or lost in, whenever it was raised. Quotes still
              in play have no closed date, so they are left out.
            </p>
          )}
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && <Loading label="Reading quotes from Autotask" />}

      {data.data && (
        <>
          <div className="grid grid-4 mb-4 pa-tiles">
            <button type="button" className={`pa-tile${!drilled ? ' is-on' : ''}`} onClick={clearDrill}>
              <Stat
                label="Quotes"
                value={num(kept.length)}
                foot={`${BASIS_WORD[basis]} ${shortDate(fromDay)} – ${shortDate(toDay)}${
                  lost.length ? ` · ${num(lost.length)} lost left out` : ''
                }`}
              />
            </button>
            <button type="button" className={`pa-tile${focus === 'won' ? ' is-on' : ''}`} onClick={() => pickFocus('won')}>
              <Stat label="Won" value={num(won.length)} foot={average(won, 'no wins yet')} />
            </button>
            <button type="button" className={`pa-tile${focus === 'lost' ? ' is-on' : ''}`} onClick={() => pickFocus('lost')}>
              <Stat label="Lost" value={num(lost.length)} foot={average(lost, 'nothing lost')} />
            </button>
            <button type="button" className={`pa-tile${focus === 'open' ? ' is-on' : ''}`} onClick={() => pickFocus('open')}>
              <Stat label="Open" value={num(open.length)} foot={average(open, 'nothing in play')} />
            </button>
            <button type="button" className={`pa-tile${focus === 'decided' ? ' is-on' : ''}`} onClick={() => pickFocus('decided')}>
              <Stat
                label="Win rate"
                value={winRate === null ? '—' : `${Math.round(winRate)}%`}
                foot={`${won.length} won · ${lost.length} lost`}
              />
            </button>
            <div className="pa-tile is-static">
              <Stat label="Won revenue" value={money(sum(won))} foot={recurring(won)} />
            </div>
            <div className="pa-tile is-static">
              <Stat label="Lost revenue" value={money(sum(lost))} foot={recurring(lost)} />
            </div>
            <div className="pa-tile is-static">
              <Stat label="Open revenue" value={money(sum(open))} foot={recurring(open)} />
            </div>
          </div>

          <ChartCard
            className="mb-4"
            title="By outcome"
            subtitle="The opportunity's stage where it says won or lost, its status otherwise. Click one to list its quotes."
          >
            <div className="pa-pick">
              {byOutcome.map((o, i) => (
                <button
                  key={o.name}
                  type="button"
                  className={outcome === o.name ? 'is-on' : undefined}
                  onClick={() => pickOutcome(o.name)}
                >
                  <BarList rows={[{ name: o.name, value: o.n, display: `${o.n} · ${money(o.value)}`, color: colour(i) }]} />
                </button>
              ))}
            </div>
          </ChartCard>

          <ChartCard
            className="mb-4"
            title={`Quotes ${BASIS_WORD[basis]} by ${byWeek ? 'week' : 'month'}`}
            subtitle={`Stacked by outcome. Click a ${byWeek ? 'week' : 'month'} to list its quotes.`}
          >
            <BarChart
              data={trend}
              xKey="period"
              stacked
              series={outcomes.map((o, i) => ({ key: o, label: o, color: colour(i) }))}
              xFormat={(v) => (byWeek ? shortDate(v) : periodLabel(v))}
              yFormat={(v) => num(v, 0)}
              height={240}
              emptyMessage={`No quotes ${BASIS_WORD[basis]} in this period.`}
              onSelect={(row) => setPeriod(period === row.period ? '' : String(row.period))}
            />
          </ChartCard>

          <section className="card">
            <div className="card-head">
              <div className="grow">
                <h3>
                  {num(rows.length)} {FOCUS_LABEL[focus]}quote{rows.length === 1 ? '' : 's'}
                  {cut.length ? ` — ${cut.join(' · ')}` : ''}
                </h3>
                <p className="sub">
                  Newest {BASIS_WORD[basis]} first. Click a quote for its line items. Value and closed date are the opportunity's.
                </p>
              </div>
              <div className="row wrap" style={{ alignItems: 'flex-end' }}>
                <Field label="Search">
                  <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Quote, company, opportunity, person" />
                </Field>
                {drilled && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={clearDrill}>
                    Clear filters
                  </button>
                )}
                <button type="button" className="btn btn-secondary btn-sm" disabled={rows.length === 0} onClick={exportCsv}>
                  Export CSV
                </button>
              </div>
            </div>
            {rows.length === 0 ? (
              <div className="card-body">
                <p className="muted small">No quotes match.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Quote</th>
                      <th>Company</th>
                      <th>Created</th>
                      <th>Closed</th>
                      <th>Outcome</th>
                      <th>Created by</th>
                      <th>Owner</th>
                      <th className="num">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((q) => (
                      <tr key={q.id}>
                        <td>
                          <button type="button" className="link-button" onClick={() => setOpenQuote(q)}>
                            {q.name || '(untitled)'}
                          </button>
                          <div className="xs muted">
                            {[q.quoteNumber !== null ? `#${q.quoteNumber}` : null, q.opportunity].filter(Boolean).join(' · ')}
                          </div>
                        </td>
                        <td>{q.company ?? '—'}</td>
                        <td className="xs">{q.created ? shortDate(q.created) : '—'}</td>
                        <td className="xs">{q.closed ? shortDate(q.closed) : '—'}</td>
                        <td>
                          {q.outcome}
                          {q.stage && <div className="xs muted">{q.stage}</div>}
                        </td>
                        <td>{q.createdBy ?? '—'}</td>
                        <td>{q.owner ?? '—'}</td>
                        <td className="num">
                          {q.amount === null ? '—' : money(q.amount)}
                          {q.monthly ? <div className="xs muted">{money(q.monthly)}/mo</div> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {openQuote && <QuoteDrawer quote={openQuote} onClose={() => setOpenQuote(null)} />}
        </>
      )}
    </>
  );
}

/** One quote: its opportunity's figures and its line items, read when opened. */
function QuoteDrawer({ quote: q, onClose }: { quote: Quote; onClose: () => void }) {
  const items = useResource<QuoteItem[]>(`/reports/quotes/${q.id}/items`);
  const required = (items.data ?? []).filter((it) => !it.optional);
  const day = (iso: string | null) => (iso ? shortDate(iso) : '—');
  return (
    <Drawer
      open
      wide
      onClose={onClose}
      title={q.name || 'Quote'}
      subtitle={[q.quoteNumber !== null ? `#${q.quoteNumber}` : null, q.company, q.outcome].filter(Boolean).join(' · ')}
    >
      <div className="grid grid-3 mb-4">
        <Stat
          label="Opportunity amount"
          value={q.amount === null ? '—' : money(q.amount)}
          foot={q.monthly ? `${money(q.monthly)} monthly` : 'no monthly revenue'}
        />
        <Stat label="Outcome" value={outcomeLabel(q)} foot={q.closed ? `closed ${day(q.closed)}` : (q.stage ?? 'no stage')} />
        <Stat
          label="Quote lines"
          value={items.data ? money(required.reduce((n, it) => n + it.total, 0)) : '…'}
          foot={items.data ? `${required.length} line${required.length === 1 ? '' : 's'}, optional ones left out` : 'reading'}
        />
      </div>
      <dl className="pa-facts mb-4">
        <dt>Opportunity</dt>
        <dd>{q.opportunity ?? '—'}</dd>
        <dt>Stage</dt>
        <dd>{q.stage ?? '—'}</dd>
        <dt>Approval status</dt>
        <dd>{q.approvalStatus}</dd>
        <dt>Created</dt>
        <dd>
          {day(q.created)}
          {q.createdBy ? ` by ${q.createdBy}` : ''}
        </dd>
        <dt>Opportunity owner</dt>
        <dd>{q.owner ?? '—'}</dd>
        <dt>Effective · expires</dt>
        <dd>
          {day(q.effective)} · {day(q.expires)}
        </dd>
        <dt>Primary quote</dt>
        <dd>
          {q.primary ? 'Yes' : 'No'}
          {q.active ? '' : ' · inactive'}
        </dd>
      </dl>
      <h4 className="mb-2">Line items</h4>
      {items.loading && <Loading label="Reading the quote's lines from Autotask" />}
      {items.error && <ErrorBanner message={items.error} />}
      {items.data && items.data.length === 0 && <p className="muted small">No lines on this quote.</p>}
      {items.data && items.data.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Type</th>
                <th className="num">Qty</th>
                <th className="num">Unit price</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.data.map((it) => (
                <tr key={it.id} className={it.optional ? 'muted' : undefined}>
                  <td>
                    {it.name || '(unnamed)'}
                    {it.optional && <span className="xs"> · optional</span>}
                    {it.description && (
                      <div className="xs muted" style={{ whiteSpace: 'pre-wrap' }}>
                        {it.description}
                      </div>
                    )}
                  </td>
                  <td className="xs">{[it.type, it.period].filter(Boolean).join(' · ') || '—'}</td>
                  <td className="num">{num(it.quantity, 2)}</td>
                  <td className="num">{money(it.unitPrice)}</td>
                  <td className="num">{money(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Drawer>
  );
}
